/**
 * Color Token System
 *
 * Defines all color-related design tokens for the EroChat application.
 * Fully compatible with the existing ColorPalette interface from
 * `src/lib/api/design.ts` and extends it with additional semantic colors.
 *
 * @module design-system/tokens/colors
 */

import type { ColorPalette } from '@/lib/api/design';

// ============================================================================
// Base Color Types
// ============================================================================

/**
 * Represents a color value in various formats.
 * Supports hex, rgb, rgba, hsl, hsla, and CSS custom property references.
 */
export type ColorValue = string;

/**
 * A color with light and dark variants.
 */
export interface ColorWithVariants {
  /** Default/base color */
  DEFAULT: ColorValue;
  /** Lighter variant */
  light: ColorValue;
  /** Darker variant */
  dark: ColorValue;
  /** Background variant (low opacity) */
  bg?: ColorValue;
}

// ============================================================================
// Brand/Primary Colors
// ============================================================================

/**
 * Brand color configuration for gradients and primary UI elements.
 */
export interface BrandColorTokens {
  /** Primary gradient start color */
  primaryGradientStart: ColorValue;
  /** Primary gradient end color */
  primaryGradientEnd: ColorValue;
  /** Secondary gradient start color */
  secondaryGradientStart: ColorValue;
  /** Secondary gradient end color */
  secondaryGradientEnd: ColorValue;
  /** Accent gradient start color (optional) */
  accentGradientStart?: ColorValue;
}

/**
 * Default brand colors matching the existing design system.
 */
export const brandColors: BrandColorTokens = {
  primaryGradientStart: '#6A0DAD',
  primaryGradientEnd: '#4B0082',
  secondaryGradientStart: '#9C27B0',
  secondaryGradientEnd: '#673AB7',
  accentGradientStart: '#B19CD9',
} as const;

// ============================================================================
// Button Colors
// ============================================================================

/**
 * Button color configuration.
 */
export interface ButtonColorTokens {
  /** Primary button background */
  primaryBg: ColorValue;
  /** Primary button hover background */
  primaryHover: ColorValue;
  /** Primary button gradient start */
  primaryGradientStart: ColorValue;
  /** Primary button gradient end */
  primaryGradientEnd: ColorValue;
  /** Secondary button background */
  secondaryBg: ColorValue;
  /** Secondary button hover background */
  secondaryHover: ColorValue;
}

/**
 * Default button colors.
 */
export const buttonColors: ButtonColorTokens = {
  primaryBg: '#E91E63',
  primaryHover: '#C2185B',
  primaryGradientStart: '#E91E63',
  primaryGradientEnd: '#FF4081',
  secondaryBg: '#424242',
  secondaryHover: '#616161',
} as const;

// ============================================================================
// Text Colors
// ============================================================================

/**
 * Text color configuration.
 */
export interface TextColorTokens {
  /** Primary text color (highest contrast) */
  primary: ColorValue;
  /** Secondary text color (medium contrast) */
  secondary: ColorValue;
  /** Accent text color (brand-colored) */
  accent: ColorValue;
  /** Muted text color (low contrast) */
  muted: ColorValue;
  /** Disabled text color */
  disabled: ColorValue;
  /** Inverted text color (for dark-on-light) */
  inverted: ColorValue;
}

/**
 * Default text colors.
 */
export const textColors: TextColorTokens = {
  primary: '#FFFFFF',
  secondary: '#E0E0E0',
  accent: '#FF4081',
  muted: '#9CA3AF',
  disabled: '#6B7280',
  inverted: '#1F2937',
} as const;

// ============================================================================
// Surface/Background Colors
// ============================================================================

/**
 * Surface and background color configuration.
 */
export interface SurfaceColorTokens {
  /** Main background color */
  background: ColorValue;
  /** Default surface color */
  surface: ColorValue;
  /** Elevated surface color (cards, modals) */
  surfaceElevated: ColorValue;
  /** Overlay background color */
  overlay: ColorValue;
  /** Card background color */
  card: ColorValue;
  /** Border color */
  border: ColorValue;
  /** Focus ring color */
  focusRing: ColorValue;
}

/**
 * Default surface colors.
 */
export const surfaceColors: SurfaceColorTokens = {
  background: '#0F0F23',
  surface: 'rgba(255, 255, 255, 0.05)',
  surfaceElevated: 'rgba(255, 255, 255, 0.1)',
  overlay: 'rgba(0, 0, 0, 0.5)',
  card: 'rgba(255, 255, 255, 0.1)',
  border: 'rgba(255, 255, 255, 0.2)',
  focusRing: '#E91E63',
} as const;

// ============================================================================
// Status/Semantic Colors
// ============================================================================

/**
 * Status color with variants for different use cases.
 */
export interface StatusColor {
  /** Default status color */
  DEFAULT: ColorValue;
  /** Light variant for backgrounds */
  light: ColorValue;
  /** Dark variant for emphasis */
  dark: ColorValue;
  /** Background variant with low opacity */
  bg: ColorValue;
}

/**
 * Status color configuration for semantic states.
 */
export interface StatusColorTokens {
  /** Success state (positive actions, confirmations) */
  success: StatusColor;
  /** Warning state (caution, attention needed) */
  warning: StatusColor;
  /** Error state (errors, destructive actions) */
  error: StatusColor;
  /** Info state (informational messages) */
  info: StatusColor;
}

/**
 * Default status colors.
 */
export const statusColors: StatusColorTokens = {
  success: {
    DEFAULT: '#10B981',
    light: '#34D399',
    dark: '#059669',
    bg: 'rgba(16, 185, 129, 0.1)',
  },
  warning: {
    DEFAULT: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    bg: 'rgba(245, 158, 11, 0.1)',
  },
  error: {
    DEFAULT: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    bg: 'rgba(239, 68, 68, 0.1)',
  },
  info: {
    DEFAULT: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
    bg: 'rgba(59, 130, 246, 0.1)',
  },
} as const;

// ============================================================================
// Rarity Colors
// ============================================================================

/**
 * Rarity color with glow effect support.
 */
export interface RarityColor {
  /** Default rarity color */
  DEFAULT: ColorValue;
  /** Background variant */
  bg: ColorValue;
  /** Glow effect color */
  glow: ColorValue;
}

/**
 * Rarity color configuration for items, achievements, and gifts.
 */
export interface RarityColorTokens {
  /** Common tier (most frequent) */
  common: RarityColor;
  /** Uncommon tier */
  uncommon: RarityColor;
  /** Rare tier */
  rare: RarityColor;
  /** Epic tier */
  epic: RarityColor;
  /** Legendary tier (most rare) */
  legendary: RarityColor;
}

/**
 * Default rarity colors matching the existing design system.
 */
export const rarityColors: RarityColorTokens = {
  common: {
    DEFAULT: '#9CA3AF',
    bg: 'rgba(156, 163, 175, 0.2)',
    glow: 'rgba(156, 163, 175, 0.4)',
  },
  uncommon: {
    DEFAULT: '#3B82F6',
    bg: 'rgba(59, 130, 246, 0.2)',
    glow: 'rgba(59, 130, 246, 0.4)',
  },
  rare: {
    DEFAULT: '#8B5CF6',
    bg: 'rgba(139, 92, 246, 0.2)',
    glow: 'rgba(139, 92, 246, 0.4)',
  },
  epic: {
    DEFAULT: '#EC4899',
    bg: 'rgba(236, 72, 153, 0.2)',
    glow: 'rgba(236, 72, 153, 0.4)',
  },
  legendary: {
    DEFAULT: '#F59E0B',
    bg: 'rgba(245, 158, 11, 0.2)',
    glow: 'rgba(245, 158, 11, 0.4)',
  },
} as const;

// ============================================================================
// Chart Colors
// ============================================================================

/**
 * Chart color configuration for data visualization.
 */
export interface ChartColorTokens {
  /** Primary chart color */
  chart1: ColorValue;
  /** Secondary chart color */
  chart2: ColorValue;
  /** Tertiary chart color */
  chart3: ColorValue;
  /** Quaternary chart color */
  chart4: ColorValue;
  /** Fifth chart color */
  chart5: ColorValue;
  /** Sixth chart color */
  chart6: ColorValue;
  /** Seventh chart color */
  chart7: ColorValue;
  /** Eighth chart color */
  chart8: ColorValue;
  /** Chart grid color */
  grid: ColorValue;
  /** Chart axis color */
  axis: ColorValue;
  /** Tooltip background */
  tooltipBg: ColorValue;
  /** Tooltip text */
  tooltipText: ColorValue;
}

type ChartColorIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
type ChartColorKey = Extract<keyof ChartColorTokens, `chart${number}`>;

const chartColorKeys = {
  1: 'chart1',
  2: 'chart2',
  3: 'chart3',
  4: 'chart4',
  5: 'chart5',
  6: 'chart6',
  7: 'chart7',
  8: 'chart8',
} satisfies Record<ChartColorIndex, ChartColorKey>;

/**
 * Default chart colors.
 */
export const chartColors: ChartColorTokens = {
  chart1: '#E91E63',
  chart2: '#9C27B0',
  chart3: '#3F51B5',
  chart4: '#00BCD4',
  chart5: '#4CAF50',
  chart6: '#FF9800',
  chart7: '#F44336',
  chart8: '#8B5CF6',
  grid: 'rgba(255, 255, 255, 0.1)',
  axis: 'rgba(255, 255, 255, 0.5)',
  tooltipBg: 'rgba(0, 0, 0, 0.8)',
  tooltipText: '#FFFFFF',
} as const;

// ============================================================================
// Swipe/Match Colors
// ============================================================================

/**
 * Swipe action color configuration.
 */
export interface SwipeColorTokens {
  /** Like action color */
  like: ColorValue;
  /** Super like action color */
  superlike: ColorValue;
  /** Dislike action color */
  dislike: ColorValue;
  /** Match indicator color */
  match: ColorValue;
}

/**
 * Default swipe colors.
 */
export const swipeColors: SwipeColorTokens = {
  like: '#10B981',
  superlike: '#FBBF24',
  dislike: '#EF4444',
  match: '#FF4081',
} as const;

// ============================================================================
// Navigation Colors
// ============================================================================

/**
 * Navigation color configuration.
 */
export interface NavigationColorTokens {
  /** Navigation text color */
  text: ColorValue;
  /** Active navigation text color */
  textActive: ColorValue;
  /** Navigation icon color */
  icon: ColorValue;
  /** Active navigation icon color */
  iconActive: ColorValue;
  /** Badge background color */
  badgeBg: ColorValue;
  /** Badge text color */
  badgeText: ColorValue;
}

/**
 * Default navigation colors.
 */
export const navigationColors: NavigationColorTokens = {
  text: '#E0E0E0',
  textActive: '#FFFFFF',
  icon: '#E0E0E0',
  iconActive: '#FF4081',
  badgeBg: '#EF4444',
  badgeText: '#FFFFFF',
} as const;

// ============================================================================
// Glass/Overlay Colors
// ============================================================================

/**
 * Glass effect color configuration for overlays and dropdowns.
 */
export interface GlassColorTokens {
  /** Glass background */
  background: ColorValue;
  /** Glass border */
  border: ColorValue;
  /** Glass highlight */
  highlight: ColorValue;
  /** Dropdown background */
  dropdownBg: ColorValue;
  /** Dropdown border */
  dropdownBorder: ColorValue;
  /** Dropdown highlight */
  dropdownHighlight: ColorValue;
  /** Dropdown shadow */
  dropdownShadow: ColorValue;
}

/**
 * Default glass colors.
 */
export const glassColors: GlassColorTokens = {
  background: 'rgba(255, 255, 255, 0.05)',
  border: 'rgba(255, 255, 255, 0.3)',
  highlight: 'rgba(255, 255, 255, 0.1)',
  dropdownBg: 'rgba(17, 24, 39, 0.9)',
  dropdownBorder: 'rgba(255, 255, 255, 0.1)',
  dropdownHighlight: 'rgba(255, 255, 255, 0.08)',
  dropdownShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
} as const;

// ============================================================================
// Post Action Colors
// ============================================================================

/**
 * Post action color configuration for social interactions.
 */
export interface ActionColorTokens {
  /** Default action color */
  default: ColorValue;
  /** Hover action color */
  hover: ColorValue;
  /** Like action */
  like: ColorValue;
  /** Like hover */
  likeHover: ColorValue;
  /** Like active */
  likeActive: ColorValue;
  /** Like glow effect */
  likeGlow: ColorValue;
  /** Comment action */
  comment: ColorValue;
  /** Comment hover */
  commentHover: ColorValue;
  /** Comment active */
  commentActive: ColorValue;
  /** View action */
  view: ColorValue;
  /** View hover */
  viewHover: ColorValue;
  /** Share action */
  share: ColorValue;
  /** Share hover */
  shareHover: ColorValue;
}

/**
 * Default action colors.
 */
export const actionColors: ActionColorTokens = {
  default: 'rgba(255, 255, 255, 0.6)',
  hover: 'rgba(255, 255, 255, 0.9)',
  like: '#F43F5E',
  likeHover: '#FB7185',
  likeActive: '#E11D48',
  likeGlow: 'rgba(244, 63, 94, 0.3)',
  comment: '#8B5CF6',
  commentHover: '#A78BFA',
  commentActive: '#7C3AED',
  view: '#06B6D4',
  viewHover: '#22D3EE',
  share: '#10B981',
  shareHover: '#34D399',
} as const;

// ============================================================================
// Online Status Colors
// ============================================================================

/**
 * Online status color configuration.
 */
export interface OnlineStatusColorTokens {
  /** Online status */
  online: ColorValue;
  /** Offline status */
  offline: ColorValue;
  /** Away status */
  away: ColorValue;
  /** Busy status */
  busy: ColorValue;
}

/**
 * Default online status colors.
 */
export const onlineStatusColors: OnlineStatusColorTokens = {
  online: '#10B981',
  offline: '#6B7280',
  away: '#F59E0B',
  busy: '#EF4444',
} as const;

// ============================================================================
// Message Colors
// ============================================================================

/**
 * Message bubble color configuration.
 */
export interface MessageColorTokens {
  /** User message background */
  userBg: ColorValue;
  /** User message text */
  userText: ColorValue;
  /** AI/Bot message background */
  aiBg: ColorValue;
  /** AI/Bot message text */
  aiText: ColorValue;
}

/**
 * Default message colors.
 */
export const messageColors: MessageColorTokens = {
  userBg: '#E91E63',
  userText: '#FFFFFF',
  aiBg: 'rgba(255, 255, 255, 0.1)',
  aiText: '#FFFFFF',
} as const;

// ============================================================================
// Complete Color Tokens Interface
// ============================================================================

/**
 * Complete color tokens structure.
 * Combines all color-related tokens into a single interface.
 */
export interface ColorTokens {
  /** Brand/gradient colors */
  brand: BrandColorTokens;
  /** Button colors */
  button: ButtonColorTokens;
  /** Text colors */
  text: TextColorTokens;
  /** Surface/background colors */
  surface: SurfaceColorTokens;
  /** Status/semantic colors */
  status: StatusColorTokens;
  /** Rarity colors */
  rarity: RarityColorTokens;
  /** Chart/data visualization colors */
  chart: ChartColorTokens;
  /** Swipe action colors */
  swipe: SwipeColorTokens;
  /** Navigation colors */
  navigation: NavigationColorTokens;
  /** Glass effect colors */
  glass: GlassColorTokens;
  /** Post action colors */
  action: ActionColorTokens;
  /** Online status colors */
  onlineStatus: OnlineStatusColorTokens;
  /** Message bubble colors */
  message: MessageColorTokens;
}

/**
 * Default color tokens combining all color-related values.
 */
export const defaultColorTokens: ColorTokens = {
  brand: brandColors,
  button: buttonColors,
  text: textColors,
  surface: surfaceColors,
  status: statusColors,
  rarity: rarityColors,
  chart: chartColors,
  swipe: swipeColors,
  navigation: navigationColors,
  glass: glassColors,
  action: actionColors,
  onlineStatus: onlineStatusColors,
  message: messageColors,
} as const;

// ============================================================================
// Legacy API Compatibility
// ============================================================================

/**
 * Creates a ColorPalette object compatible with the existing design API.
 * Maps the new token system to the legacy ColorPalette interface from `src/lib/api/design.ts`.
 *
 * @param tokens - Optional color tokens to convert (defaults to defaultColorTokens)
 * @returns ColorPalette object compatible with the design API
 */
export function createLegacyColorPalette(tokens: ColorTokens = defaultColorTokens): ColorPalette {
  // Build the base palette with required fields
  const palette: ColorPalette = {
    // Brand/gradient colors
    primary_gradient_start: tokens.brand.primaryGradientStart,
    primary_gradient_end: tokens.brand.primaryGradientEnd,
    secondary_gradient_start: tokens.brand.secondaryGradientStart,
    secondary_gradient_end: tokens.brand.secondaryGradientEnd,

    // Button colors
    primary_button_gradient_start: tokens.button.primaryGradientStart,
    primary_button_gradient_end: tokens.button.primaryGradientEnd,
    primary_button_bg: tokens.button.primaryBg,
    primary_button_hover: tokens.button.primaryHover,
    secondary_button_bg: tokens.button.secondaryBg,
    secondary_button_hover: tokens.button.secondaryHover,

    // Text colors
    primary_text: tokens.text.primary,
    secondary_text: tokens.text.secondary,
    accent_text: tokens.text.accent,

    // Surface colors
    card_background: tokens.surface.card,
    border_color: tokens.surface.border,

    // Status colors
    success: tokens.status.success.DEFAULT,
    warning: tokens.status.warning.DEFAULT,
    error: tokens.status.error.DEFAULT,
    info: tokens.status.info.DEFAULT,

    // Chart colors
    chart_1: tokens.chart.chart1,
    chart_2: tokens.chart.chart2,
    chart_3: tokens.chart.chart3,
    chart_4: tokens.chart.chart4,
    chart_5: tokens.chart.chart5,
    chart_6: tokens.chart.chart6,
    chart_7: tokens.chart.chart7,
    chart_8: tokens.chart.chart8,

    // Rarity colors
    rarity_common: tokens.rarity.common.DEFAULT,
    rarity_uncommon: tokens.rarity.uncommon.DEFAULT,
    rarity_rare: tokens.rarity.rare.DEFAULT,
    rarity_epic: tokens.rarity.epic.DEFAULT,
    rarity_legendary: tokens.rarity.legendary.DEFAULT,

    // Swipe colors
    swipe_like: tokens.swipe.like,
    swipe_superlike: tokens.swipe.superlike,
    swipe_dislike: tokens.swipe.dislike,

    // Navigation colors
    nav_icon_active: tokens.navigation.iconActive,
    nav_badge_bg: tokens.navigation.badgeBg,

    // Action colors
    action_like: tokens.action.like,
    action_like_active: tokens.action.likeActive,
    action_comment: tokens.action.comment,
    action_view: tokens.action.view,

    // Glass colors
    glass_dropdown_bg: tokens.glass.dropdownBg,
    glass_dropdown_border: tokens.glass.dropdownBorder,
    glass_dropdown_highlight: tokens.glass.dropdownHighlight,
  };

  // Add optional fields only if they have values
  // This is required for exactOptionalPropertyTypes compatibility
  if (tokens.brand.accentGradientStart) {
    palette.gradient_accent_start = tokens.brand.accentGradientStart;
  }

  return palette;
}

// ============================================================================
// CSS Custom Properties Generation
// ============================================================================

/**
 * Generates CSS custom properties from color tokens.
 * These can be injected into the DOM for runtime theming.
 *
 * @param tokens - Color tokens to convert to CSS custom properties
 * @returns Record of CSS variable names to values
 */
export function generateColorCSSVariables(tokens: ColorTokens = defaultColorTokens): Record<string, string> {
  return {
    // Brand/gradient colors
    '--color-primary-gradient-start': tokens.brand.primaryGradientStart,
    '--color-primary-gradient-end': tokens.brand.primaryGradientEnd,
    '--color-secondary-gradient-start': tokens.brand.secondaryGradientStart,
    '--color-secondary-gradient-end': tokens.brand.secondaryGradientEnd,
    '--color-gradient-accent-start': tokens.brand.accentGradientStart ?? tokens.brand.secondaryGradientStart,

    // Button colors
    '--color-primary-button': tokens.button.primaryBg,
    '--color-primary-button-hover': tokens.button.primaryHover,
    '--color-secondary-button': tokens.button.secondaryBg,
    '--color-secondary-button-hover': tokens.button.secondaryHover,
    '--gradient-button-start': tokens.button.primaryGradientStart,
    '--gradient-button-end': tokens.button.primaryGradientEnd,

    // Text colors
    '--color-primary-text': tokens.text.primary,
    '--color-secondary-text': tokens.text.secondary,
    '--color-accent-text': tokens.text.accent,
    '--color-muted-text': tokens.text.muted,
    '--color-disabled-text': tokens.text.disabled,

    // Surface colors
    '--color-background': tokens.surface.background,
    '--color-surface': tokens.surface.surface,
    '--color-surface-elevated': tokens.surface.surfaceElevated,
    '--color-overlay': tokens.surface.overlay,
    '--color-card-background': tokens.surface.card,
    '--color-border': tokens.surface.border,
    '--color-focus-ring': tokens.surface.focusRing,

    // Status colors
    '--color-success': tokens.status.success.DEFAULT,
    '--color-success-light': tokens.status.success.light,
    '--color-success-dark': tokens.status.success.dark,
    '--color-success-bg': tokens.status.success.bg,
    '--color-warning': tokens.status.warning.DEFAULT,
    '--color-warning-light': tokens.status.warning.light,
    '--color-warning-dark': tokens.status.warning.dark,
    '--color-warning-bg': tokens.status.warning.bg,
    '--color-error': tokens.status.error.DEFAULT,
    '--color-error-light': tokens.status.error.light,
    '--color-error-dark': tokens.status.error.dark,
    '--color-error-bg': tokens.status.error.bg,
    '--color-info': tokens.status.info.DEFAULT,
    '--color-info-light': tokens.status.info.light,
    '--color-info-dark': tokens.status.info.dark,
    '--color-info-bg': tokens.status.info.bg,

    // Rarity colors
    '--color-rarity-common': tokens.rarity.common.DEFAULT,
    '--color-rarity-common-bg': tokens.rarity.common.bg,
    '--color-rarity-common-glow': tokens.rarity.common.glow,
    '--color-rarity-uncommon': tokens.rarity.uncommon.DEFAULT,
    '--color-rarity-uncommon-bg': tokens.rarity.uncommon.bg,
    '--color-rarity-uncommon-glow': tokens.rarity.uncommon.glow,
    '--color-rarity-rare': tokens.rarity.rare.DEFAULT,
    '--color-rarity-rare-bg': tokens.rarity.rare.bg,
    '--color-rarity-rare-glow': tokens.rarity.rare.glow,
    '--color-rarity-epic': tokens.rarity.epic.DEFAULT,
    '--color-rarity-epic-bg': tokens.rarity.epic.bg,
    '--color-rarity-epic-glow': tokens.rarity.epic.glow,
    '--color-rarity-legendary': tokens.rarity.legendary.DEFAULT,
    '--color-rarity-legendary-bg': tokens.rarity.legendary.bg,
    '--color-rarity-legendary-glow': tokens.rarity.legendary.glow,

    // Chart colors
    '--color-chart-1': tokens.chart.chart1,
    '--color-chart-2': tokens.chart.chart2,
    '--color-chart-3': tokens.chart.chart3,
    '--color-chart-4': tokens.chart.chart4,
    '--color-chart-5': tokens.chart.chart5,
    '--color-chart-6': tokens.chart.chart6,
    '--color-chart-7': tokens.chart.chart7,
    '--color-chart-8': tokens.chart.chart8,
    '--color-chart-grid': tokens.chart.grid,
    '--color-chart-axis': tokens.chart.axis,
    '--color-chart-tooltip-bg': tokens.chart.tooltipBg,
    '--color-chart-tooltip-text': tokens.chart.tooltipText,

    // Swipe colors
    '--color-swipe-like': tokens.swipe.like,
    '--color-swipe-superlike': tokens.swipe.superlike,
    '--color-swipe-dislike': tokens.swipe.dislike,
    '--color-swipe-match': tokens.swipe.match,

    // Navigation colors
    '--color-nav-text': tokens.navigation.text,
    '--color-nav-text-active': tokens.navigation.textActive,
    '--color-nav-icon': tokens.navigation.icon,
    '--color-nav-icon-active': tokens.navigation.iconActive,
    '--color-nav-badge-bg': tokens.navigation.badgeBg,
    '--color-nav-badge-text': tokens.navigation.badgeText,

    // Glass colors
    '--color-glass-background': tokens.glass.background,
    '--color-glass-border': tokens.glass.border,
    '--color-glass-highlight': tokens.glass.highlight,
    '--glass-dropdown-bg': tokens.glass.dropdownBg,
    '--glass-dropdown-border': tokens.glass.dropdownBorder,
    '--glass-dropdown-highlight': tokens.glass.dropdownHighlight,
    '--glass-dropdown-shadow': tokens.glass.dropdownShadow,

    // Action colors
    '--color-action-default': tokens.action.default,
    '--color-action-hover': tokens.action.hover,
    '--color-action-like': tokens.action.like,
    '--color-action-like-hover': tokens.action.likeHover,
    '--color-action-like-active': tokens.action.likeActive,
    '--color-action-like-glow': tokens.action.likeGlow,
    '--color-action-comment': tokens.action.comment,
    '--color-action-comment-hover': tokens.action.commentHover,
    '--color-action-comment-active': tokens.action.commentActive,
    '--color-action-view': tokens.action.view,
    '--color-action-view-hover': tokens.action.viewHover,
    '--color-action-share': tokens.action.share,
    '--color-action-share-hover': tokens.action.shareHover,

    // Online status colors
    '--color-status-online': tokens.onlineStatus.online,
    '--color-status-offline': tokens.onlineStatus.offline,
    '--color-status-away': tokens.onlineStatus.away,
    '--color-status-busy': tokens.onlineStatus.busy,

    // Message colors
    '--color-message-user-bg': tokens.message.userBg,
    '--color-message-user-text': tokens.message.userText,
    '--color-message-ai-bg': tokens.message.aiBg,
    '--color-message-ai-text': tokens.message.aiText,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Gets a rarity color by tier name.
 *
 * @param tier - Rarity tier name
 * @param tokens - Color tokens to use
 * @returns Rarity color object
 */
export function getRarityColor(tier: keyof RarityColorTokens, tokens: ColorTokens = defaultColorTokens): RarityColor {
  return tokens.rarity[tier];
}

/**
 * Gets a status color by type.
 *
 * @param type - Status type
 * @param tokens - Color tokens to use
 * @returns Status color object
 */
export function getStatusColor(type: keyof StatusColorTokens, tokens: ColorTokens = defaultColorTokens): StatusColor {
  return tokens.status[type];
}

/**
 * Gets a chart color by index (1-8).
 *
 * @param index - Chart color index (1-8)
 * @param tokens - Color tokens to use
 * @returns Chart color value
 */
export function getChartColor(index: ChartColorIndex, tokens: ColorTokens = defaultColorTokens): ColorValue {
  const key = chartColorKeys[index];
  return tokens.chart[key];
}

// ============================================================================
// Exports
// ============================================================================

export {
  brandColors as defaultBrandColors,
  buttonColors as defaultButtonColors,
  textColors as defaultTextColors,
  surfaceColors as defaultSurfaceColors,
  statusColors as defaultStatusColors,
  rarityColors as defaultRarityColors,
  chartColors as defaultChartColors,
  swipeColors as defaultSwipeColors,
  navigationColors as defaultNavigationColors,
  glassColors as defaultGlassColors,
  actionColors as defaultActionColors,
  onlineStatusColors as defaultOnlineStatusColors,
  messageColors as defaultMessageColors,
};
